import type { Asset } from '../types/domain';

export type QualityControlStatus = 'passed' | 'failed' | 'pending';

export type QualityControlReextractMode = 'particles_only' | 'content_and_particles';

export type QuestionImageCorrelation = 'strong' | 'moderate' | 'weak' | 'none';

export type AssetQualityControlMetadata = {
  overall_passed?: boolean;
  last_status?: string;
  last_checked_at?: string;
  reextract_mode?: QualityControlReextractMode;
  image_content?: {
    passed?: boolean;
    image_subject?: string | null;
    extraction_category?: string | null;
    subject_deviation?: string | null;
    subject_deviation_reason?: string | null;
    content_fidelity_score?: number | null;
    content_fidelity_summary?: string | null;
    issues?: string[];
  };
  particle_alignment?: {
    passed?: boolean;
    score?: number | null;
    summary?: string | null;
    issues?: string[];
    particle_count?: number;
    particle_subjects?: string[];
    content_scope?: string | null;
    answer_leakage_detected?: boolean;
    answer_leakage_summary?: string | null;
  };
  multimodal_analysis?: {
    recommended?: boolean;
    question_image_correlation?: QuestionImageCorrelation;
    reason?: string | null;
    visual_condition_gap_detected?: boolean;
    visual_condition_gap_summary?: string | null;
    missing_condition_warning_detected?: boolean;
  };
  /** Additive：几何图专项评分（不参与 overall_passed） */
  geometry_figure_qa?: {
    detected?: boolean;
    question_related?: boolean;
    score?: number | null;
    summary?: string | null;
    assessed_at?: string;
    assessment_source?: 'vision_model' | 'skipped';
  } | null;
  subject_corrected?: boolean;
  subject_corrected_to?: string | null;
  subject_corrected_from_extraction?: string | null;
};

function normalizeAssetMetadata(metadata: unknown): Record<string, unknown> | null {
  if (!metadata) return null;
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }
  if (typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata as Record<string, unknown>;
  }
  return null;
}

export function readAssetQualityControl(asset?: Asset | null): AssetQualityControlMetadata | null {
  const metadata = normalizeAssetMetadata(asset?.metadata);
  if (!metadata) return null;
  const qc = metadata.quality_control;
  if (!qc || typeof qc !== 'object') return null;
  return qc as AssetQualityControlMetadata;
}

export function getQualityControlStatusFromMetadata(
  qualityControl?: AssetQualityControlMetadata | null,
): QualityControlStatus {
  if (!qualityControl?.last_checked_at) return 'pending';
  return qualityControl.overall_passed ? 'passed' : 'failed';
}

export function resolveQualityControlStatus(
  qualityControl?: AssetQualityControlMetadata | null,
  asset?: Asset | null,
): QualityControlStatus {
  return getQualityControlStatusFromMetadata(qualityControl ?? readAssetQualityControl(asset));
}

export function mergeQualityControlIntoAsset(
  asset: Asset,
  qualityControl?: AssetQualityControlMetadata | null,
): Asset {
  if (!qualityControl) return asset;
  const metadata = normalizeAssetMetadata(asset.metadata) || {};
  return {
    ...asset,
    metadata: {
      ...metadata,
      quality_control: qualityControl,
    },
  };
}

export function getQualityControlStatus(asset?: Asset | null): QualityControlStatus {
  return resolveQualityControlStatus(null, asset);
}

const STALE_QC_SERVICE_ERROR = /subject_deviation is not defined/i;
const RETRYABLE_QC_EXECUTION_ERROR = /timeout of \d+ms exceeded|ECONNABORTED|ETIMEDOUT|socket hang up|QA FC 请求失败|network error|fetch failed/i;
const INFORMATIONAL_QC_NOTE = /符合规范|符合要求|符合预期|未见.*泄露|未直接暴露|未暴露.*答案|不存在.*泄露|无泄露|未泄露|不属于.*泄露|不算泄露|不应判为|无需|无问题|未见异常|属于正常|正常结构|结构合理|做法正确|符合.*规范/i;

export function isInformationalQualityControlNote(message: string): boolean {
  return INFORMATIONAL_QC_NOTE.test(String(message || '').trim());
}

export function filterDisplayQualityControlIssues(issues?: string[] | null): string[] {
  return (issues || [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .filter((item) => !isInformationalQualityControlNote(item));
}

function isTransientQualityControlErrorMessage(message: string): boolean {
  const normalized = String(message || '').trim();
  if (!normalized) {
    return false;
  }
  return STALE_QC_SERVICE_ERROR.test(normalized) || RETRYABLE_QC_EXECUTION_ERROR.test(normalized);
}

export function isRetryableQualityControlExecutionError(
  qualityControl?: AssetQualityControlMetadata | null,
): boolean {
  if (!qualityControl?.last_checked_at) {
    return false;
  }
  if (qualityControl.last_status === 'error') {
    return true;
  }
  return collectQualityControlErrorMessages(qualityControl)
    .some(isTransientQualityControlErrorMessage);
}

export function isStaleQualityControlServiceError(
  qualityControl?: AssetQualityControlMetadata | null,
): boolean {
  const messages = collectQualityControlErrorMessages(qualityControl);
  return messages.some((message) => STALE_QC_SERVICE_ERROR.test(message));
}

function collectQualityControlErrorMessages(
  qualityControl?: AssetQualityControlMetadata | null,
): string[] {
  return [
    qualityControl?.image_content?.content_fidelity_summary,
    qualityControl?.particle_alignment?.summary,
    ...(qualityControl?.image_content?.issues || []),
    ...(qualityControl?.particle_alignment?.issues || []),
  ]
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

/** 尚未品控，或历史品控执行失败（超时、服务 bug 等），需要主动重跑 */
export function shouldAutoRunQualityControl(
  qualityControl?: AssetQualityControlMetadata | null,
  options?: { particleCount?: number },
): boolean {
  if (typeof options?.particleCount === 'number' && options.particleCount <= 0) {
    return false;
  }

  if (!qualityControl?.last_checked_at) {
    return true;
  }
  return isRetryableQualityControlExecutionError(qualityControl);
}

export function isQualityControlPendingExtraction(particleCount: number | undefined): boolean {
  return typeof particleCount === 'number' && particleCount <= 0;
}

export function isQualityControlResultReady(
  qualityControl?: AssetQualityControlMetadata | null,
  options?: {
    baselineCheckedAt?: string | null;
    untilNotStale?: boolean;
  },
): boolean {
  if (!qualityControl?.last_checked_at) {
    return false;
  }

  const baselineCheckedAt = String(options?.baselineCheckedAt || '').trim();
  if (baselineCheckedAt) {
    const nextCheckedAt = new Date(qualityControl.last_checked_at).getTime();
    const baselineAt = new Date(baselineCheckedAt).getTime();
    if (Number.isFinite(nextCheckedAt) && Number.isFinite(baselineAt) && nextCheckedAt <= baselineAt) {
      return false;
    }
  }

  if (options?.untilNotStale && isRetryableQualityControlExecutionError(qualityControl)) {
    return false;
  }

  return true;
}

export function formatQualityControlUserMessage(message: string): string {
  if (STALE_QC_SERVICE_ERROR.test(message)) {
    return '品控服务异常导致历史检测失败，系统将自动重新检测';
  }
  if (/timeout of \d+ms exceeded/i.test(message)) {
    return '品控服务响应超时（视觉模型处理较慢），请稍后重试';
  }
  return message;
}

export function formatQualityControlRunErrorMessage(error?: string | null): string {
  const normalized = String(error || '').trim();
  if (!normalized) {
    return '品控未能完成，请稍后重试';
  }

  if (/timeout of \d+ms exceeded/i.test(normalized)) {
    return '品控请求超时，请确认网络正常后重试';
  }

  if (/ASSET_EXTRACTION_QA_FC_BASE_URL/i.test(normalized) || /未配置/.test(normalized)) {
    return '品控服务未配置，请联系管理员';
  }
  if (/无可用内容提取|跳过 QA/i.test(normalized)) {
    return '暂无内容提取记录，请先完成素材提取';
  }
  if (/oss_id/i.test(normalized)) {
    return '素材缺少图片信息，无法执行品控';
  }
  if (/asset not found/i.test(normalized)) {
    return '素材不存在或已删除';
  }

  return formatQualityControlUserMessage(normalized);
}

/** 品控执行失败（非「内容未通过」的业务结论） */
export function isQualityControlExecutionError(
  qualityControl?: AssetQualityControlMetadata | null,
): boolean {
  if (!qualityControl?.last_checked_at) {
    return false;
  }
  return qualityControl.last_status === 'error' || isRetryableQualityControlExecutionError(qualityControl);
}

export function resolveQualityControlFailureSummary(
  qualityControl?: AssetQualityControlMetadata | null,
  runError?: string | null,
): string | null {
  if (!qualityControl?.last_checked_at) {
    return runError ? formatQualityControlRunErrorMessage(runError) : null;
  }

  const display = sanitizeQualityControlForDisplay(qualityControl);
  const imageContent = display?.image_content;
  const particleAlignment = display?.particle_alignment;

  if (isQualityControlExecutionError(qualityControl)) {
    const messages = collectQualityControlErrorMessages(display)
      .map(formatQualityControlUserMessage)
      .filter(Boolean);
    if (messages.length) {
      return messages[0];
    }
    return runError ? formatQualityControlRunErrorMessage(runError) : '品控执行失败';
  }

  if (qualityControl.overall_passed === false) {
    const candidates = [
      imageContent?.subject_deviation_reason,
      imageContent?.content_fidelity_summary,
      particleAlignment?.summary,
      ...(imageContent?.issues || []),
      ...(particleAlignment?.issues || []),
    ]
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    if (candidates.length) {
      return candidates[0];
    }
    return '品控未通过，请查看下方详情';
  }

  return runError ? formatQualityControlRunErrorMessage(runError) : null;
}

function formatQualityControlStatusLabel(passed?: boolean): string {
  if (passed === true) return '通过';
  if (passed === false) return '未通过';
  return '—';
}

function normalizeSubjectToken(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function subjectTokenIsMath(token: string): boolean {
  if (!token) return false;
  return token === 'math'
    || token === '数学'
    || token.includes('数学')
    || token.includes('math');
}

/** 图片学科 / 提取学科 / 颗粒学科任一为数学时，才展示几何品控。 */
export function isMathQualityControlSubject(
  qualityControl?: AssetQualityControlMetadata | null,
): boolean {
  const tokens = [
    qualityControl?.image_content?.image_subject,
    qualityControl?.image_content?.extraction_category,
    qualityControl?.subject_corrected_to,
    ...(qualityControl?.particle_alignment?.particle_subjects || []),
  ]
    .map(normalizeSubjectToken)
    .filter(Boolean);
  if (!tokens.length) return false;
  return tokens.some(subjectTokenIsMath);
}

export function hasGeometryFigureQaSignal(
  geometry?: AssetQualityControlMetadata['geometry_figure_qa'],
): boolean {
  if (!geometry || typeof geometry !== 'object') return false;
  return geometry.detected != null
    || geometry.question_related != null
    || geometry.score != null
    || Boolean(String(geometry.summary || '').trim())
    || Boolean(geometry.assessment_source);
}

/** 仅数学学科展示几何专项区块（有无评分都展示判断结论）。 */
export function shouldShowGeometryFigureQaSection(
  qualityControl?: AssetQualityControlMetadata | null,
): boolean {
  return isMathQualityControlSubject(qualityControl);
}

export function formatGeometryFigureQaCopyLines(
  geometry?: AssetQualityControlMetadata['geometry_figure_qa'],
): string[] {
  if (!hasGeometryFigureQaSignal(geometry)) {
    return ['几何图专项：本次品控未包含几何判断，可重新运行品控获取'];
  }
  const lines = ['几何图专项'];
  lines.push(`检测到几何图：${geometry?.detected ? '是' : '否'}`);
  lines.push(`与题目相关：${geometry?.question_related ? '是' : '否'}`);
  if (geometry?.score != null && Number.isFinite(Number(geometry.score))) {
    lines.push(`几何图 QA 分：${Number(geometry.score).toFixed(2)}`);
  } else {
    lines.push('几何图 QA 分：未评分（未满足评分条件）');
  }
  if (geometry?.summary) {
    lines.push(String(geometry.summary));
  }
  return lines;
}

export function formatQualityControlCopyText(
  qualityControl?: AssetQualityControlMetadata | null,
): string {
  const displayQualityControl = sanitizeQualityControlForDisplay(qualityControl);
  if (!displayQualityControl?.last_checked_at) {
    return '';
  }

  const lines: string[] = ['品质管控'];
  const imageContent = displayQualityControl.image_content;
  const particleAlignment = displayQualityControl.particle_alignment;

  lines.push(`原图与内容提取：${formatQualityControlStatusLabel(imageContent?.passed)}`);
  if (imageContent?.image_subject || imageContent?.extraction_category) {
    lines.push(`图片学科 ${imageContent?.image_subject || '—'} · 提取学科 ${imageContent?.extraction_category || '—'}`);
  }
  if (imageContent?.subject_deviation_reason) {
    lines.push(imageContent.subject_deviation_reason);
  }
  if (imageContent?.content_fidelity_summary) {
    lines.push(imageContent.content_fidelity_summary);
  }

  lines.push('');
  lines.push(`内容提取与知识颗粒：${formatQualityControlStatusLabel(particleAlignment?.passed)}`);
  if (particleAlignment?.summary) {
    lines.push(particleAlignment.summary);
  }
  if (typeof particleAlignment?.particle_count === 'number') {
    const suffix = particleAlignment.content_scope === 'single_question'
      ? '（单题素材，1 个颗粒属正常）'
      : '';
    lines.push(`关联颗粒数：${particleAlignment.particle_count}${suffix}`);
  }
  if (particleAlignment?.answer_leakage_detected) {
    lines.push('题干疑似包含答案');
    lines.push(particleAlignment.answer_leakage_summary || '请检查题干是否把最终答案、正确选项或结论写进了题目本身。');
  }
  if (displayQualityControl.subject_corrected) {
    lines.push(`已自动纠正学科：${displayQualityControl.subject_corrected_from_extraction || '—'} → ${displayQualityControl.subject_corrected_to || '—'}`);
  }

  if (shouldShowGeometryFigureQaSection(displayQualityControl)) {
    lines.push('');
    lines.push(...formatGeometryFigureQaCopyLines(displayQualityControl.geometry_figure_qa));
  }

  const issues = [
    ...filterDisplayQualityControlIssues(imageContent?.issues),
    ...filterDisplayQualityControlIssues(particleAlignment?.issues),
  ];
  if (issues.length) {
    lines.push('');
    lines.push('问题：');
    issues.forEach((issue) => {
      lines.push(`• ${issue}`);
    });
  }

  return lines.join('\n').trim();
}

export function sanitizeQualityControlForDisplay(
  qualityControl?: AssetQualityControlMetadata | null,
): AssetQualityControlMetadata | null {
  if (!qualityControl) return null;

  const imageContent = qualityControl.image_content;
  const particleAlignment = qualityControl.particle_alignment;

  return {
    ...qualityControl,
    image_content: imageContent
      ? {
          ...imageContent,
          content_fidelity_summary: imageContent.content_fidelity_summary
            ? formatQualityControlUserMessage(imageContent.content_fidelity_summary)
            : imageContent.content_fidelity_summary,
          issues: (imageContent.issues || []).map(formatQualityControlUserMessage),
        }
      : imageContent,
    particle_alignment: particleAlignment
      ? {
          ...particleAlignment,
          summary: particleAlignment.summary
            ? formatQualityControlUserMessage(particleAlignment.summary)
            : particleAlignment.summary,
          issues: (particleAlignment.issues || []).map(formatQualityControlUserMessage),
        }
      : particleAlignment,
  };
}

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function normalizeQuestionImageCorrelation(value: unknown): QuestionImageCorrelation {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'strong' || normalized === 'moderate' || normalized === 'weak' || normalized === 'none') {
    return normalized;
  }
  return 'none';
}

function buildMultimodalAnalysisFromQa(qa: Record<string, unknown>) {
  return {
    recommended: Boolean(qa.multimodal_analysis_recommended),
    question_image_correlation: normalizeQuestionImageCorrelation(qa.question_image_correlation),
    reason: (qa.multimodal_analysis_reason as string) || null,
    visual_condition_gap_detected: Boolean(qa.visual_condition_gap_detected),
    visual_condition_gap_summary: (qa.visual_condition_gap_summary as string) || null,
    missing_condition_warning_detected: Boolean(qa.missing_condition_warning_detected),
  };
}

function buildGeometryFigureQaFromQa(
  qa: Record<string, unknown>,
  checkedAt: string,
): AssetQualityControlMetadata['geometry_figure_qa'] {
  const nested = qa.geometry_figure_qa;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const geometry = nested as Record<string, unknown>;
    const scoreRaw = geometry.score ?? geometry.geometry_figure_qa_score;
    const sourceRaw = String(
      geometry.assessment_source || geometry.geometry_figure_qa_assessment_source || '',
    ).trim();
    return {
      detected: Boolean(geometry.detected ?? geometry.geometry_figure_detected),
      question_related: Boolean(geometry.question_related ?? geometry.geometry_question_related),
      score: scoreRaw != null && Number.isFinite(Number(scoreRaw)) ? Number(scoreRaw) : null,
      summary: String(geometry.summary || geometry.geometry_figure_qa_summary || '').trim() || null,
      assessed_at: checkedAt,
      assessment_source: sourceRaw === 'vision_model' ? 'vision_model' : 'skipped',
    };
  }

  const hasSignal = qa.geometry_figure_detected != null
    || qa.geometry_question_related != null
    || qa.geometry_figure_qa_score != null
    || String(qa.geometry_figure_qa_summary || '').trim()
    || String(qa.geometry_figure_qa_assessment_source || '').trim();
  if (!hasSignal) return null;

  return {
    detected: Boolean(qa.geometry_figure_detected),
    question_related: Boolean(qa.geometry_question_related),
    score: qa.geometry_figure_qa_score != null && Number.isFinite(Number(qa.geometry_figure_qa_score))
      ? Number(qa.geometry_figure_qa_score)
      : null,
    summary: String(qa.geometry_figure_qa_summary || '').trim() || null,
    assessed_at: checkedAt,
    assessment_source: String(qa.geometry_figure_qa_assessment_source || '') === 'vision_model'
      ? 'vision_model'
      : 'skipped',
  };
}

function buildQualityControlFromQaRecord(record: Record<string, unknown>): AssetQualityControlMetadata | null {
  const checkedAt = String(record.created_at || '').trim();
  if (!checkedAt) return null;

  const qaStatus = String(record.qa_status || 'error').trim();
  const errorMessage = String(record.error_message || '').trim();

  if (qaStatus === 'error') {
    const summary = errorMessage || '品控执行失败';
    return {
      overall_passed: false,
      last_status: 'error',
      last_checked_at: checkedAt,
      image_content: {
        passed: false,
        image_subject: (record.image_subject as string) || null,
        extraction_category: (record.extraction_category as string) || null,
        subject_deviation: (record.subject_deviation as string) || null,
        subject_deviation_reason: (record.subject_deviation_reason as string) || null,
        content_fidelity_score: record.content_fidelity_score != null ? Number(record.content_fidelity_score) : null,
        content_fidelity_summary: (record.content_fidelity_summary as string) || summary,
        issues: summary ? [summary] : [],
      },
      particle_alignment: {
        passed: false,
        summary,
        issues: [],
      },
      reextract_mode: 'particles_only',
    };
  }

  const qaResult = record.qa_result && typeof record.qa_result === 'object'
    ? record.qa_result as Record<string, unknown>
    : {};
  const qa = (qaResult.qa && typeof qaResult.qa === 'object'
    ? qaResult.qa
    : qaResult) as Record<string, unknown>;
  const overallPassed = Boolean(qa.passed ?? record.qa_passed);

  return {
    overall_passed: overallPassed,
    last_status: qaStatus || (overallPassed ? 'passed' : 'failed'),
    last_checked_at: checkedAt,
    image_content: {
      passed: Boolean(qa.image_content_passed ?? record.qa_passed),
      image_subject: (qa.image_subject as string) || (record.image_subject as string) || null,
      extraction_category: (qa.extraction_category as string) || (record.extraction_category as string) || null,
      subject_deviation: (qa.subject_deviation as string) || (record.subject_deviation as string) || null,
      subject_deviation_reason: (qa.subject_deviation_reason as string) || (record.subject_deviation_reason as string) || null,
      content_fidelity_score: qa.content_fidelity_score != null
        ? Number(qa.content_fidelity_score)
        : record.content_fidelity_score != null
          ? Number(record.content_fidelity_score)
          : null,
      content_fidelity_summary: (qa.content_fidelity_summary as string) || (record.content_fidelity_summary as string) || null,
      issues: normalizeStringArray(qa.issues).length
        ? normalizeStringArray(qa.issues)
        : normalizeStringArray(record.issues),
    },
    particle_alignment: {
      passed: Boolean(qa.particle_alignment_passed ?? record.qa_passed),
      score: qa.particle_alignment_score != null ? Number(qa.particle_alignment_score) : null,
      summary: (qa.particle_alignment_summary as string) || null,
      issues: normalizeStringArray(qa.particle_alignment_issues),
      particle_count: Number(qa.particle_count) || 0,
      particle_subjects: normalizeStringArray(qa.particle_subjects),
      content_scope: (qa.content_scope as string) || null,
      answer_leakage_detected: Boolean(qa.answer_leakage_detected),
      answer_leakage_summary: (qa.answer_leakage_summary as string) || null,
    },
    multimodal_analysis: buildMultimodalAnalysisFromQa(qa),
    geometry_figure_qa: buildGeometryFigureQaFromQa(qa, checkedAt),
    reextract_mode: 'particles_only',
  };
}

export function resolveAssetQualityControlPayload(input: {
  quality_control?: AssetQualityControlMetadata | Record<string, unknown> | null;
  latest_records?: unknown[];
}): AssetQualityControlMetadata | null {
  const stored = input.quality_control && typeof input.quality_control === 'object'
    ? input.quality_control as AssetQualityControlMetadata
    : null;

  const latestRecord = Array.isArray(input.latest_records) ? input.latest_records[0] : null;
  const fromRecord = latestRecord && typeof latestRecord === 'object'
    ? buildQualityControlFromQaRecord(latestRecord as Record<string, unknown>)
    : null;

  if (stored?.last_checked_at) {
    // 旧 metadata 可能缺几何专项；用最新 QA 记录回填，避免弹窗空白。
    if (!hasGeometryFigureQaSignal(stored.geometry_figure_qa)
      && hasGeometryFigureQaSignal(fromRecord?.geometry_figure_qa)) {
      return {
        ...stored,
        geometry_figure_qa: fromRecord?.geometry_figure_qa ?? null,
      };
    }
    return stored;
  }

  return fromRecord || stored;
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export type PollAssetQualityControlOptions = {
  maxWaitMs?: number;
  intervalMs?: number;
  baselineCheckedAt?: string | null;
  untilNotStale?: boolean;
};

export async function pollAssetQualityControl(
  fetchPayload: () => Promise<{
    quality_control?: AssetQualityControlMetadata | Record<string, unknown> | null;
    latest_records?: unknown[];
  }>,
  options?: PollAssetQualityControlOptions,
): Promise<AssetQualityControlMetadata | null> {
  const maxWaitMs = options?.maxWaitMs ?? 180000;
  const intervalMs = options?.intervalMs ?? 15_000;
  const startedAt = Date.now();

  while (Date.now() - startedAt < maxWaitMs) {
    const payload = await fetchPayload().catch(() => null);
    const resolved = payload ? resolveAssetQualityControlPayload(payload) : null;
    if (isQualityControlResultReady(resolved, options)) {
      return resolved;
    }
    await sleep(intervalMs);
  }

  const finalPayload = await fetchPayload().catch(() => null);
  const finalResolved = finalPayload ? resolveAssetQualityControlPayload(finalPayload) : null;
  return isQualityControlResultReady(finalResolved, options) ? finalResolved : finalResolved;
}
